import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import '../styles/components/legal-pages.scss';

const PrivacyPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <PageHeader 
        title="PRIVACY POLICY" 
        subtitle="How we collect, use, and protect your personal data"
      />
      <div className="legal-page-container">
        <div className="legal-content">

        <section>
          <h2>1. Introduction</h2>
          <p>
            Northern Championship ApS ("we", "us", or "our") operates the Northern Championship Series website (ncseries.gg). 
            This Privacy Policy informs you of our policies regarding the collection, use, and disclosure of personal data 
            when you use our Service and the choices you have associated with that data.
          </p>
          <p>
            We are committed to protecting your privacy and complying with the General Data Protection Regulation (GDPR) 
            and Danish data protection laws.
          </p>
        </section>

        <section>
          <h2>2. Data Controller</h2>
          <p>
            Northern Championship ApS is the data controller for the personal data processed through our Service.
            Contact us at: <a href="mailto:admin@ncseries.gg">admin@ncseries.gg</a>
          </p>
        </section>

        <section>
          <h2>3. Legal Basis for Processing</h2>
          <p>We process your personal data based on the following legal grounds:</p>
          <ul>
            <li><strong>Consent:</strong> For marketing communications and optional data processing</li>
            <li><strong>Contract:</strong> To provide tournament services and fulfill our obligations to participants</li>
            <li><strong>Legitimate Interests:</strong> For improving our services, fraud prevention, and security</li>
            <li><strong>Legal Obligations:</strong> To comply with Danish laws and regulations</li>
          </ul>
        </section>

        <section>
          <h2>4. Data We Collect</h2>
          
          <h3>4.1 Account Information</h3>
          <ul>
            <li>Steam ID and authentication data</li>
            <li>Username and display name</li>
            <li>Email address</li>
            <li>Date of birth (for age verification)</li>
            <li>Profile picture (from Steam or uploaded)</li>
          </ul>

          <h3>4.2 Gaming Data</h3>
          <ul>
            <li>Counter-Strike match statistics and performance data</li>
            <li>In-game statistics (K/D ratio, ADR, rating, etc.)</li>
            <li>Match history and results</li>
            <li>Team affiliations and roles</li>
            <li>Tournament participation history</li>
          </ul>

          <h3>4.3 Discord Data</h3>
          <ul>
            <li>Discord username and ID</li>
            <li>Server membership status</li>
            <li>Roles and permissions within our Discord server</li>
            <li>Messages sent in tournament-related channels</li>
          </ul>

          <h3>4.4 Communication Data</h3>
          <ul>
            <li>Support ticket contents</li>
            <li>Email correspondence</li>
            <li>Tournament communications</li>
            <li>Social media handles (Twitter, Twitch, etc.)</li>
          </ul>

          <h3>4.5 Technical Data</h3>
          <ul>
            <li>IP address</li>
            <li>Browser type and version</li>
            <li>Device information</li>
            <li>Operating system</li>
            <li>Referral source</li>
            <li>Pages visited and interaction data</li>
          </ul>

          <h3>4.6 Event Data (for LAN participants)</h3>
          <ul>
            <li>Real name (for registration)</li>
            <li>Emergency contact information</li>
            <li>Dietary restrictions or accessibility needs</li>
            <li>Photos and video footage from events</li>
          </ul>

          <h3>4.7 Financial Data</h3>
          <ul>
            <li>Bank account details (for prize payments)</li>
            <li>Tax identification information (CPR number for Danish residents)</li>
            <li>Payment transaction history</li>
          </ul>
        </section>

        <section>
          <h2>5. How We Use Your Data</h2>
          <p>We use the collected data for various purposes:</p>
          <ul>
            <li>To provide and maintain our Service</li>
            <li>To organize and manage tournaments</li>
            <li>To process prize payments</li>
            <li>To communicate about tournaments and events</li>
            <li>To improve our Service and user experience</li>
            <li>To detect and prevent cheating or fraud</li>
            <li>To comply with legal obligations</li>
            <li>To create match broadcasts and content</li>
            <li>To maintain competitive integrity</li>
            <li>For statistical analysis and reporting</li>
          </ul>
        </section>

        <section>
          <h2>6. Data Sharing and Disclosure</h2>
          <p>We may share your data with:</p>
          
          <h3>6.1 Service Providers</h3>
          <ul>
            <li>PowerStats (for match statistics processing)</li>
            <li>Steam API (for authentication and game data)</li>
            <li>Discord (for community features)</li>
            <li>Umami Analytics (for privacy-focused website analytics)</li>
            <li>Streaming platforms (Twitch, YouTube)</li>
            <li>Payment processors</li>
            <li>Cloud hosting providers</li>
          </ul>

          <h3>6.2 Public Information</h3>
          <p>The following information is publicly visible:</p>
          <ul>
            <li>Player profiles and statistics</li>
            <li>Match results and tournament standings</li>
            <li>Team rosters and information</li>
            <li>Stream broadcasts of matches</li>
          </ul>

          <h3>6.3 Legal Requirements</h3>
          <p>
            We may disclose your data if required by Danish law, court orders, or government regulations.
          </p>
        </section>

        <section>
          <h2>7. International Data Transfers</h2>
          <p>
            Your data may be transferred to and processed in countries outside the EU/EEA. When we do so, 
            we ensure appropriate safeguards are in place, such as:
          </p>
          <ul>
            <li>EU Standard Contractual Clauses</li>
            <li>Adequacy decisions by the European Commission</li>
            <li>Your explicit consent</li>
          </ul>
        </section>

        <section>
          <h2>8. Data Retention</h2>
          <p>We retain your personal data for:</p>
          <ul>
            <li><strong>Account data:</strong> As long as your account is active</li>
            <li><strong>Tournament data:</strong> Indefinitely for historical records</li>
            <li><strong>Financial data:</strong> 5 years for tax compliance</li>
            <li><strong>Communication data:</strong> 2 years</li>
            <li><strong>Technical data:</strong> 1 year</li>
          </ul>
          <p>
            After these periods, data is either deleted or anonymized for statistical purposes.
          </p>
        </section>

        <section>
          <h2>9. Your Rights Under GDPR</h2>
          <p>As a data subject, you have the following rights:</p>
          <ul>
            <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
            <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
            <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
            <li><strong>Right to Restrict Processing:</strong> Request limited processing of your data</li>
            <li><strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
            <li><strong>Right to Object:</strong> Object to certain types of processing</li>
            <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
          </ul>
          <p>
            To exercise these rights, contact us at <a href="mailto:admin@ncseries.gg">admin@ncseries.gg</a>. We will respond within 30 days.
          </p>
        </section>

        <section>
          <h2>10. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your data, including:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security assessments</li>
            <li>Access controls and authentication</li>
            <li>Regular backups and disaster recovery procedures</li>
            <li>Employee training on data protection</li>
          </ul>
        </section>

        <section>
          <h2>11. Children's Privacy</h2>
          <p>
            Our Service is not intended for children under 16. We do not knowingly collect data from children 
            under 16 without parental consent. If you are a parent and believe your child has provided us with 
            personal data, please contact us.
          </p>
        </section>

        <section>
          <h2>12. Analytics and Tracking</h2>
          <p>
            We use Umami Analytics, a privacy-focused, open-source analytics platform that helps us understand how
            visitors use our website. Umami is designed with privacy in mind and does not collect any personally
            identifiable information (PII). It is fully GDPR-compliant and does not use cookies for tracking purposes.
          </p>
          <p>
            <strong>What Umami collects:</strong>
          </p>
          <ul>
            <li>Page views and navigation patterns (anonymized)</li>
            <li>Device type and browser information (general categories only)</li>
            <li>Referral sources (where visitors came from)</li>
            <li>Geographic location (country/region level only, not precise location)</li>
            <li>Custom events for user interactions (e.g., button clicks, without personal data)</li>
          </ul>
          <p>
            <strong>What Umami does NOT collect:</strong>
          </p>
          <ul>
            <li>IP addresses (they are anonymized immediately)</li>
            <li>Personal information or user accounts</li>
            <li>Cross-site tracking or user fingerprinting</li>
            <li>Any data that can be used to identify you personally</li>
          </ul>

          <h3>12.1 Logged-In User Analytics</h3>
          <p>
            When you authenticate via Steam, we may associate your analytics session with your Steam ID to provide
            better service and understand how authenticated users interact with our platform. This helps us:
          </p>
          <ul>
            <li>Improve features for registered users</li>
            <li>Identify and fix issues affecting specific user groups</li>
            <li>Understand usage patterns to enhance the platform</li>
            <li>Provide better support when you contact us</li>
          </ul>
          <p>
            Your Steam ID is a <strong>public identifier</strong> already visible on your Steam profile. When you log in,
            we link your analytics session to your Steam ID along with basic information such as your username and role
            (user/admin). We do not collect additional personal information beyond what is in your Steam profile and what
            you voluntarily provide through our platform.
          </p>
          <p>
            <strong>Important:</strong> This identification only occurs when you are logged in. Anonymous visitors who
            browse our site without logging in remain completely anonymous.
          </p>

          <p>
            Because Umami does not use cookies or collect personal data, no cookie consent banner is required for
            our analytics implementation. All data is aggregated and anonymized, ensuring your privacy while helping
            us improve the Northern Championships Series platform.
          </p>
          <p>
            For more information about cookies and tracking technologies, please see our <Link to="/cookies">Cookie Policy</Link>.
          </p>
        </section>

        <section>
          <h2>13. Third-Party Links</h2>
          <p>
            Our Service may contain links to third-party websites. We are not responsible for the privacy practices 
            of these external sites. We encourage you to read their privacy policies.
          </p>
        </section>

        <section>
          <h2>14. Changes to This Policy</h2>
          <p>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the 
            new Privacy Policy on this page and updating the "Last updated" date. For material changes, we will 
            provide additional notice via email.
          </p>
        </section>

        <section>
          <h2>15. Data Protection Officer</h2>
          <p>
            For questions about our data processing activities, you can contact our Data Protection Officer at: <a href="mailto:admin@ncseries.gg">admin@ncseries.gg</a>
          </p>
        </section>

        <section>
          <h2>16. Supervisory Authority</h2>
          <p>
            If you are not satisfied with our response to your concerns, you have the right to lodge a complaint with:
          </p>
          <div className="contact-info">
            <p>Datatilsynet (Danish Data Protection Agency)</p>
            <p>Carl Jacobsens Vej 35</p>
            <p>2500 Valby, Denmark</p>
            <p>Phone: +45 33 19 32 00</p>
            <p>Email: dt@datatilsynet.dk</p>
          </div>
        </section>
        
        <p className="legal-date">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;